import {
    createCall,
    Method,
    WsCall,
    TokenChecks,
    Token,
    VoidParser,
    WsPageableList,
    PageableList,
} from '@hugendubel/ws_client';
import { WsAddressList } from './result/WsAddressList';
import { WsAddress } from './result/WsAddress';
import { WsInitResult } from './result/WsInitResult';
import { WsStockInfo } from './result/WsStockInfo';
import { WsBonusShopOrder } from './result/WsBonusShopOrder';
import { WsBonusShopHistory } from './result/WsBonusShopHistory';
import { WsArticle, Article } from '@hugendubel/article';
import { ReducedProduct, ReducedProductResult } from './result/ReducedProduct';

export enum ProductFilter {
    NO_DOWNLOADS = 'noDownloads',
    ONLY_DOWNLOADS = 'onlyDownloads',
}
type OrderSystemCode = 'PR' | 'PE';

export const isAuthenticated = (token?: Token): boolean =>
    TokenChecks.isCustomer(token) || TokenChecks.isBonusShopCustomer(token);

const _private = <T extends WsCall<any>>(call: T): T => ({ ...call, tokenCheck: isAuthenticated });

export const WS = {
    article: {
        articleByEan: createCall<{ ean: string }, Article>({
            name: 'article/articlebyean',
            parser: WsArticle.parse,
        }),
    },
    customer: {
        updateBonusParticipation: createCall<{ bonusProgram: boolean }, void>(
            _private({
                name: 'customer/updatebonusparticipation',
                parser: VoidParser,
                method: Method.POST,
            })
        ),
    },
    oneTimeToken: {
        createPasswordResetToken: createCall<{ email: string; languageCode: 'de' }, void>({
            name: 'onetimetoken/createpasswordresettoken',
            parser: VoidParser,
        }),
    },
    init: createCall<{ uuid?: string; url: string }, WsInitResult>({
        name: 'bonusshop/initjwt',
        parser: WsInitResult.fromApi,
        method: Method.POST,
    }),

    search: createCall<
        {
            maxPoints?: number;
            offset: number;
            maxResults: number;
            eanFilter?: string;
            sortField: string;
            ascending: boolean;
            productFilter?: ProductFilter;
        },
        PageableList<ReducedProduct>
    >({
        name: 'bonusshop/search',
        parser: WsPageableList.parserFor(ReducedProductResult.parse),
        method: Method.POST,
    }),

    listDelivery: createCall<void, WsAddressList>(
        _private({
            name: 'bonusshop/listdelivery',
            parser: WsAddressList.fromApi,
            method: Method.POST,
        })
    ),

    createAddress: createCall<
        {
            salutation: string;
            firstName: string;
            lastName: string;
            companyName: string;
            addition?: string;
            department?: string;
            poBox?: string;
            street: string;
            houseNumber: string;
            zipCode: string;
            city: string;
            phone?: string;
            mobilePhone?: string;
        },
        WsAddress
    >(
        _private({
            name: 'bonusshop/createaddress',
            parser: WsAddress.fromApi,
            method: Method.POST,
        })
    ),

    orderForDelivery: createCall<
        { articleId: number; deliveryAddressId: string; orderSystemCode: OrderSystemCode },
        WsBonusShopOrder
    >(
        _private({
            name: 'bonusshop/orderfordelivery',
            parser: WsBonusShopOrder.fromApi,
            method: Method.POST,
        })
    ),

    orderForPickup: createCall<{ articleId: number; orderSystemCode: OrderSystemCode }, WsBonusShopOrder>(
        _private({
            name: 'bonusshop/orderforpickup',
            parser: WsBonusShopOrder.fromApi,
            method: Method.POST,
        })
    ),

    loadStockInfo: createCall<{ eanList: string }, WsStockInfo>(
        _private({
            name: 'bonusshop/stockinfo',
            parser: WsStockInfo.fromApi,
            method: Method.POST,
        })
    ),

    loadBonusPointHistory: createCall<void, WsBonusShopHistory>(
        _private({
            name: 'bonusshop/transactionhistory',
            parser: WsBonusShopHistory.fromApi,
            method: Method.POST,
        })
    ),
};
